import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Card, Typography, CircularProgress, Tooltip, IconButton,
} from '@mui/material';
import { Refresh } from '@mui/icons-material';
import apiClient from 'utils/ApiClient';
import { infralytiqsAnalyticsSitePathSegment } from 'utils/infralytiqsAnalyticsPath';
import { ReportDefinition, TimeRangeDef } from '../Settings/reportDsl';

interface CounterTileProps {
  report: ReportDefinition;
  tenantId: string;
  siteId?: string;
  /**
   * Optional global override for the panel's time range — typically the
   * Report Suite toolbar's "Time Range" selection. The counter is
   * intentionally tied to that selection so users always see "X for the
   * range I'm currently looking at".
   */
  timeRangeOverride?: TimeRangeDef;
  timezone?: string;
}

/**
 * Compact "counter" tile used by toolbar-mode reports.
 *
 * Mirrors the pre-existing `number` visualization but in a tile that's
 * sized to live inline in the Reports toolbar (next to the suite + time
 * pickers). Reuses `/il/analytics/.../report-query` so the metric/filters
 * authored in the Report Suite editor drive what gets counted.
 */
export const CounterTile: React.FC<CounterTileProps> = ({
  report, tenantId, siteId, timeRangeOverride, timezone,
}) => {
  const pathSite = useMemo(() => infralytiqsAnalyticsSitePathSegment(siteId), [siteId]);
  const [value, setValue] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const metricLabel = report.query.metrics?.[0]?.label
    || report.visualizations?.[0]?.title
    || report.name
    || 'Total';

  const fetchValue = useCallback(async () => {
    if (!tenantId) return;
    setLoading(true);
    setError(null);
    try {
      const body: Record<string, any> = {
        // Toolbar tiles are always a single aggregate — strip out any
        // accidental dimensions / granularity from the saved DSL so the
        // counter is a single scalar even if the author left those in.
        query: {
          ...report.query,
          dimensions: [],
          timeRange: { ...(report.query.timeRange || {}), granularity: undefined },
        },
        runtimeFilters: [],
        timeRangeOverride,
      };
      if (timezone) body.timezone = timezone;
      const res: any = await apiClient.post(
        `/il/analytics/${tenantId}/${pathSite}/report-query`,
        body,
      );
      const rows: Array<Record<string, any>> = res?.data ?? [];
      const raw = rows[0]?.metric_0;
      const n = typeof raw === 'string' ? Number(raw) : Number(raw ?? 0);
      setValue(Number.isFinite(n) ? n : 0);
    } catch (err: any) {
      setError(err?.message || 'Failed to load');
      setValue(null);
    } finally {
      setLoading(false);
    }
  }, [report.query, tenantId, pathSite, timeRangeOverride, timezone]);

  useEffect(() => { fetchValue(); }, [fetchValue]);

  return (
    <Tooltip title={report.description || metricLabel}>
      <Card
        variant="outlined"
        sx={{
          borderRadius: 2,
          px: 1.5,
          py: 0.75,
          minWidth: 160,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          backgroundColor: (t) => t.palette.mode === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(255,255,255,0.7)',
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', lineHeight: 1.1, fontWeight: 600,
                  whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
          >
            {metricLabel}
          </Typography>
          {loading ? (
            <CircularProgress size={16} sx={{ mt: 0.25 }} />
          ) : error ? (
            <Typography variant="body2" color="error" sx={{ fontWeight: 700 }}>—</Typography>
          ) : (
            <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.1 }} color="secondary">
              {(value ?? 0).toLocaleString()}
            </Typography>
          )}
        </Box>
        <Tooltip title="Refresh">
          <IconButton size="small" onClick={fetchValue} aria-label="Refresh counter">
            <Refresh fontSize="inherit" />
          </IconButton>
        </Tooltip>
      </Card>
    </Tooltip>
  );
};
